import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { backendConfigured, getConfig, getLiveState, simReset, simSetMcap } from '@/lib/api';
import { forceStartRailwayAudio, resumeRailwayAudio, setRailwayAudioScene, startRailwayAudio, stopRailwayAudio, suspendRailwayAudio } from '@/lib/sound';

const LiveContext = createContext(null);

const STATIC_MINT = (process.env.REACT_APP_TOKEN_MINT_ADDRESS || '').trim();
const STATIC_BUY_URL = (process.env.REACT_APP_BUY_URL || '').trim();

const FALLBACK_CONFIG = {
  placeholders: {
    TOKEN_NAME: 'THOMAS COIN', TOKEN_TICKER: 'THOMAS', CONTRACT_ADDRESS: STATIC_MINT,
    TOKEN_MINT_ADDRESS: STATIC_MINT,
    BUY_URL: STATIC_BUY_URL || (STATIC_MINT ? `https://pump.fun/coin/${STATIC_MINT}` : ''),
    NETWORK: 'SOLANA', LAUNCH_PLATFORM: 'PUMP.FUN', LAUNCH_DATE: '13 AUGUST 2026',
    X_URL: 'https://x.com/ThomasCoin_sol', TELEGRAM_URL: 'https://t.me/thomasportal_sol',
  },
  previewMode: false,
};

const FALLBACK_STATE = {
  currentMcapUsd: null,
  verifiedAthMcapUsd: null,
  routeProgress: 0,
  stationId: 'S_DEPOT',
  stationName: 'THOMAS DEPOT',
  passengerCoachCount: 3,
  goldWagonCount: 0,
  goldWagonVisible: 0,
  goldWagonOverflow: 0,
  goldWagonRemainder: 0,
  maxVisibleGoldWagons: 3,
  currentStationIndex: 0,
  unlockedStopCount: 0,
  loopEligible: false,
  routeVersion: 'solana-line-v3',
  status: STATIC_MINT ? 'live' : 'unavailable',
  source: 'static-site',
  mode: 'live',
};

export function LiveProvider({ children }) {
  const [state, setState] = useState(FALLBACK_STATE);
  const [config, setConfig] = useState(FALLBACK_CONFIG);
  const [error, setError] = useState(false);
  const [ticketOpen, setTicketOpen] = useState(false);
  const [soundOn, setSoundOn] = useState(false);
  const [soundLoading, setSoundLoading] = useState(false);
  const prefersReduced =
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const [reducedMotion, setReducedMotion] = useState(prefersReduced);
  const pollRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const s = await getLiveState();
      setState(s);
      setError(false);
    } catch {
      setError(true);
      setState((current) => current || FALLBACK_STATE);
    }
  }, []);

  useEffect(() => {
    if (!backendConfigured) return undefined;
    (async () => {
      try {
        const c = await getConfig();
        setConfig(c);
      } catch {
        setError(true);
        setConfig(FALLBACK_CONFIG);
      }
      refresh();
    })();
    pollRef.current = setInterval(refresh, 20000);
    return () => clearInterval(pollRef.current);
  }, [refresh]);

  useEffect(() => {
    const sections = Array.from(document.querySelectorAll('[data-audio-scene]'));
    if (!sections.length || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver((entries) => {
      const activeEntry = entries.find((entry) => entry.isIntersecting);
      if (activeEntry?.target?.dataset.audioScene) setRailwayAudioScene(activeEntry.target.dataset.audioScene);
    }, { rootMargin: '-42% 0px -42% 0px', threshold: 0 });
    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const onVisibility = () => {
      if (document.hidden) suspendRailwayAudio();
      else resumeRailwayAudio();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  useEffect(() => {
    let enabled = false;
    let starting = false;
    const events = ['click', 'touchend', 'keydown'];
    const removeUnlock = () => events.forEach((event) => window.removeEventListener(event, unlock, true));
    const unlock = async (event) => {
      if (event?.target?.closest?.('[data-testid="sound-toggle-button"]')) return;
      if (enabled || starting) return;
      starting = true;
      const started = await forceStartRailwayAudio();
      starting = false;
      if (started) {
        enabled = true;
        setSoundOn(true);
        setSoundLoading(false);
        removeUnlock();
      }
    };
    events.forEach((event) => window.addEventListener(event, unlock, { capture: true, passive: true }));
    startRailwayAudio().then((started) => {
      if (started) {
        enabled = true;
        setSoundOn(true);
        removeUnlock();
      }
    });
    return removeUnlock;
  }, []);

  const setSim = useCallback(async (mcap) => {
    try {
      const s = await simSetMcap(mcap);
      setState(s);
      return s;
    } catch {
      return null;
    }
  }, []);

  const resetSim = useCallback(async () => {
    try {
      const s = await simReset();
      setState(s);
      return s;
    } catch {
      return null;
    }
  }, []);

  const toggleSound = useCallback(async (event) => {
    event?.stopPropagation?.();
    if (soundOn) {
      stopRailwayAudio();
      setSoundOn(false);
      return;
    }
    setSoundLoading(true);
    const started = await forceStartRailwayAudio();
    setSoundOn(started);
    setSoundLoading(false);
  }, [soundOn]);

  const value = {
    state,
    config,
    error,
    refresh,
    setSim,
    resetSim,
    ticketOpen,
    setTicketOpen,
    soundOn,
    soundLoading,
    toggleSound,
    reducedMotion,
    setReducedMotion,
  };

  return <LiveContext.Provider value={value}>{children}</LiveContext.Provider>;
}

export function useLive() {
  const ctx = useContext(LiveContext);
  if (!ctx) throw new Error('useLive outside LiveProvider');
  return ctx;
}
